import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import { botRunnerFiles, playMatch, roundRobin, type BotDriver, type Pairing, type Replay, type Winner } from "@buildlab/shared/arena";

// Bot Arena tournaments (decision 99), run in the teacher's browser. Every bot is student code, so
// each one plays from its own iframe with PREVIEW_SANDBOX ("allow-scripts" only: an opaque origin
// with no access to the app, its cookies or the network; the preview CSP blocks every request),
// built by buildPreview so its loops stop themselves after LOOP_BUDGET_MS. The app keeps the rules
// and asks each frame for a move by postMessage; playMatch gives it ARENA.moveMs to answer. A bot
// that doesn't answer in time is removed (frame.remove(); Chrome runs sandboxed frames in their own
// process, so that also ends a runaway one) and stands still for the rest of the match. A fresh
// frame per bot per match, so `memory` and globals never carry over. Nothing here runs on the
// server or in the app's own origin.

/** Loops in a bot stop after this long (they throw, so the bot crashes that turn and is counted too slow). */
const LOOP_BUDGET_MS = 150;

/** Drives one bot in one match from a sandboxed frame inside `host`. */
export function frameDriver(host: HTMLElement, code: string): BotDriver {
  let frame: HTMLIFrameElement | null = null;
  let nextId = 1;
  let onReady: ((ok: boolean) => void) | null = null;
  const pending = new Map<number, { resolve: (dir: unknown) => void; reject: (e: Error) => void }>();
  const onMessage = (e: MessageEvent) => {
    if (!frame || e.source !== frame.contentWindow) return;
    const m = e.data as { __arena?: number; kind?: string; id?: number; dir?: unknown; ok?: boolean; crashed?: boolean } | null;
    if (!m || m.__arena !== 1) return;
    if (m.kind === "ready") onReady?.(!!m.ok);
    else if (m.kind === "move" && typeof m.id === "number") {
      const p = pending.get(m.id);
      if (!p) return;
      pending.delete(m.id);
      if (m.crashed) p.reject(new Error("The bot crashed."));
      else p.resolve(m.dir);
    }
  };
  return {
    start: () =>
      new Promise<boolean>((resolve) => {
        onReady = (ok) => {
          onReady = null;
          resolve(ok);
        };
        window.addEventListener("message", onMessage);
        frame = document.createElement("iframe");
        frame.setAttribute("sandbox", PREVIEW_SANDBOX);
        frame.setAttribute("title", "A bot playing");
        frame.setAttribute("aria-hidden", "true");
        frame.tabIndex = -1;
        frame.srcdoc = buildPreview(botRunnerFiles(code), { loopBudgetMs: LOOP_BUDGET_MS }).html;
        host.appendChild(frame);
      }),
    move: (view) =>
      new Promise((resolve, reject) => {
        const id = nextId++;
        pending.set(id, { resolve, reject });
        frame?.contentWindow?.postMessage({ __arena: 1, kind: "move", id, view }, "*");
      }),
    stop: () => {
      window.removeEventListener("message", onMessage);
      frame?.remove();
      frame = null;
      pending.clear();
    },
  };
}

export interface TournamentBot {
  /** An entry id, or a house bot's id. */
  id: string;
  code: string;
}

export interface PlayedMatch extends Pairing {
  replay: Replay;
  aCoins: number;
  bCoins: number;
  winner: Winner;
}

/**
 * Plays every match of the round robin, one at a time (each match is its own pair of frames), and
 * reports each one as it finishes. Resolves with the matches in round-robin order, ready to save.
 */
export async function runTournament(opts: {
  bots: TournamentBot[];
  seed: number;
  host: HTMLElement;
  /** Matches at once. 1 by default: bots in other matches can share a process and slow each other down. */
  lanes?: number;
  onMatch?: (done: number, total: number, match: PlayedMatch) => void;
  cancelled?: () => boolean;
}): Promise<PlayedMatch[]> {
  const code = new Map(opts.bots.map((b) => [b.id, b.code]));
  const pairs = roundRobin(opts.bots.map((b) => b.id), opts.seed);
  const out: PlayedMatch[] = new Array(pairs.length);
  let next = 0;
  let done = 0;
  const lane = async () => {
    while (next < pairs.length) {
      if (opts.cancelled?.()) throw new Error("The tournament was stopped.");
      const i = next++;
      const p = pairs[i]!;
      const r = await playMatch(p.seed, [frameDriver(opts.host, code.get(p.a)!), frameDriver(opts.host, code.get(p.b)!)]);
      const replay: Replay = { v: 1, seed: r.seed, a: r.a, b: r.b, ...(r.notes ? { notes: r.notes } : {}) };
      out[i] = { ...p, replay, aCoins: r.aCoins, bCoins: r.bCoins, winner: r.winner };
      opts.onMatch?.(++done, pairs.length, out[i]!);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(opts.lanes ?? 1, pairs.length)) }, lane));
  return out;
}
